import {
  AssessmentQuestionResponseFormat,
  type ReviewPageCompetency,
} from "@/types/screener-builder";
import { ScreenerType } from "@/types/screeners";
import type { Nullable } from "@/types/utils";

export const LEGACY_CLOSING_MESSAGE_PREFIX = "This concludes our interview. ";

export const isScreener30Type = (
  screenerType?: Nullable<ScreenerType>,
): boolean =>
  screenerType === ScreenerType.v30 || screenerType === ScreenerType.v30m;

export const shouldApplyLegacyClosingMessagePrefix = (
  screenerType?: Nullable<ScreenerType>,
): boolean => !isScreener30Type(screenerType);

export const getEditableClosingMessage = (
  message: string,
  screenerType?: Nullable<ScreenerType>,
): string => {
  if (
    shouldApplyLegacyClosingMessagePrefix(screenerType) &&
    message.startsWith(LEGACY_CLOSING_MESSAGE_PREFIX)
  ) {
    return message.substring(LEGACY_CLOSING_MESSAGE_PREFIX.length);
  }

  return message;
};

export const getPersistedClosingMessage = (
  message: string,
  screenerType?: Nullable<ScreenerType>,
): string =>
  shouldApplyLegacyClosingMessagePrefix(screenerType)
    ? `${LEGACY_CLOSING_MESSAGE_PREFIX}${message}`
    : message;

export const getSelectedResponseFormat = (
  competency: ReviewPageCompetency,
): AssessmentQuestionResponseFormat =>
  competency.questions.find((q) => q.isSelected)?.responseFormat ??
  AssessmentQuestionResponseFormat.Written;

export const isVoiceResponseFormat = (
  format: AssessmentQuestionResponseFormat,
): boolean =>
  format === AssessmentQuestionResponseFormat.VoiceRecommended ||
  format === AssessmentQuestionResponseFormat.VoiceRequired;

export const isVoiceCompetency = (competency: ReviewPageCompetency): boolean =>
  isVoiceResponseFormat(getSelectedResponseFormat(competency));

/**
 * Whether the Languages dropdown may be shown on the review page.
 *
 * AS-2032 made it reachable after publish, because adding a language is what
 * retypes a 3.0 screener to 3.0m. Two guards keep that from being wider than
 * the ticket:
 *
 * - `isReadOnly` is the predicate every other control on this page honors; the
 *   dropdown has no `disabled` state of its own, so this is the only thing
 *   keeping it consistent with its siblings on a published legacy screener.
 * - post-publish editing is limited to 3.0 types. On a published 2.0ai
 *   screener the same save would retype it to 2.0aim, which changes template
 *   synthesis and triggers translations — a much larger change than this
 *   ticket asks for. Drafts of every type keep the pre-AS-2032 behavior.
 */
export const canEditScreenerLanguages = (
  hasLanguageDropdownFeatureFlag: boolean,
  isReadOnly: boolean,
  isPublished: boolean,
  screenerType: Nullable<ScreenerType> | undefined,
): boolean =>
  hasLanguageDropdownFeatureFlag &&
  !isReadOnly &&
  (!isPublished || isScreener30Type(screenerType));
